<script setup>
/**
 * 開課就緒分數 pill + tooltip（表單／商品／活動共用）
 */
defineProps({
    readiness: {
        type: Object,
        default: null
    },
    compact: {
        type: Boolean,
        default: false
    }
});
</script>

<template>
    <a-tooltip
        placement="topLeft"
        overlay-class-name="readiness-tooltip-overlay"
        :mouse-enter-delay="0.15"
    >
        <template #title>
            <div v-if="readiness?.checks?.length" class="readiness-tip">
                <div class="readiness-tip__head">
                    開課就緒檢查
                    <span class="readiness-tip__score">
                        {{ readiness.score }}/{{ readiness.total }}
                        <template v-if="readiness.forms_count != null">
                            · 表單 {{ readiness.ready_forms_count }}/{{
                                readiness.forms_count
                            }}
                        </template>
                    </span>
                </div>
                <ul class="readiness-tip__list">
                    <li
                        v-for="c in readiness.checks"
                        :key="c.key"
                        class="readiness-tip__item"
                        :class="{
                            'readiness-tip__item--ok': c.ok,
                            'readiness-tip__item--bad': !c.ok
                        }"
                    >
                        <span class="readiness-tip__mark" aria-hidden="true">
                            {{ c.ok ? '✓' : '!' }}
                        </span>
                        <span class="readiness-tip__body">
                            <span class="readiness-tip__label">
                                {{ c.label }}
                            </span>
                            <span v-if="c.hint" class="readiness-tip__hint">
                                {{ c.hint }}
                            </span>
                        </span>
                    </li>
                </ul>
            </div>
            <span v-else>尚無就緒資訊</span>
        </template>
        <span
            class="readiness-pill"
            :class="[
                readiness?.ready
                    ? 'readiness-pill--ready'
                    : 'readiness-pill--warn',
                { 'readiness-pill--compact': compact }
            ]"
        >
            {{ readiness ? `${readiness.score}/${readiness.total}` : '—' }}
        </span>
    </a-tooltip>
</template>

<style scoped>
.readiness-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.5rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.02em;
    cursor: help;
    line-height: 1.4;
}

.readiness-pill--compact {
    min-width: 2.1rem;
    font-size: 11px;
    padding: 0.05rem 0.35rem;
}

.readiness-pill--ready {
    color: #2f6b45;
    background: #e7f5ec;
    border: 1px solid #b7dfc5;
}

.readiness-pill--warn {
    color: #8a6d1d;
    background: #fbf3d8;
    border: 1px solid #f0df9a;
}
</style>

<style>
/* tooltip 掛在 body，不可 scoped */
.readiness-tooltip-overlay .ant-tooltip-inner {
    max-width: 320px;
    padding: 10px 12px;
    background: #fff;
    color: #2a2a3a;
    box-shadow: 0 8px 24px rgba(40, 30, 70, 0.16);
}

.readiness-tooltip-overlay .ant-tooltip-arrow::before {
    background: #fff;
}

.readiness-tip__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-weight: 700;
    margin-bottom: 8px;
    font-size: 13px;
}

.readiness-tip__score {
    font-weight: 800;
    color: #2a4a7a;
    font-size: 12px;
}

.readiness-tip__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.readiness-tip__item {
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 8px;
    align-items: start;
    font-size: 12px;
    line-height: 1.45;
}

.readiness-tip__mark {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    margin-top: 1px;
}

.readiness-tip__item--ok .readiness-tip__mark {
    color: #3d6b4f;
    background: #e8f3ec;
}

.readiness-tip__item--bad .readiness-tip__mark {
    color: #8a6d1d;
    background: #fbf3d8;
}

.readiness-tip__body {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.readiness-tip__label {
    font-weight: 600;
    color: #2a2a3a;
}

.readiness-tip__hint {
    color: #8a8a9a;
    font-size: 11px;
    word-break: break-word;
}
</style>
